import { type RefObject, useLayoutEffect, useState } from 'react'

/**
 * Returns whether the referenced element's content overflows its horizontal axis
 * (`scrollWidth > clientWidth`). Re-measures via ResizeObserver on element resize and
 * via MutationObserver on character-data changes. Returns `undefined` until the ref is
 * attached and measured.
 *
 * Mirrors the legacy `packages/web/src/hooks/useIsOverflowing.ts`. Copied rather than
 * shared because `common-all` is React-free and no shared React-utility package exists yet.
 */
export const useIsOverflowing = <T extends HTMLElement>(
  ref: RefObject<T | null>,
  enabled = true,
): boolean | undefined => {
  const [isOverflow, setIsOverflow] = useState<boolean | undefined>(undefined)

  // Run after layout so the first paint reflects the truth. ResizeObserver picks up width
  // changes (window resize, container reflow); MutationObserver picks up text changes that
  // don't change the box size but DO change scrollWidth (e.g. a wider single character).
  //
  // The `enabled` flag exists so callers (Tooltip in particular) can call this hook
  // unconditionally to satisfy Rules of Hooks while paying zero observer cost when the
  // result will be discarded. When `enabled` is false the effect attaches nothing.
  useLayoutEffect(() => {
    if (!enabled) {
      return
    }
    const element = ref.current
    if (!element) {
      return
    }

    const measure = (): void => {
      setIsOverflow(element.scrollWidth > element.clientWidth)
    }

    measure()

    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(element)

    const mutationObserver = new MutationObserver(measure)
    mutationObserver.observe(element, {
      characterData: true,
      childList: true,
      subtree: true,
    })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [ref, enabled])

  return isOverflow
}
